import {Box, Show, VStack} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {
  GetMetadaServiceCaseApiResponse,
  GetServiceCaseOrdersApiResponse,
  useGetServiceCaseCustomerMessagesQuery,
} from '@dms/api/workshop';
import {
  CommunicationChannelChips,
  CommunicationThreadComposer,
  CommunicationThreadFeed,
  useCommunicationThreadMessages,
  useThreadCallActions,
} from '@dms/shared';

import {getServiceCaseContactPhoneNumber} from '../utils/getServiceCaseContactPhoneNumber';

interface ServiceCaseCommunicationThreadTabProps {
  serviceCaseId: string;
  serviceCase: GetMetadaServiceCaseApiResponse;
  serviceOrders: GetServiceCaseOrdersApiResponse;
}

export function ServiceCaseCommunicationThreadTab(props: ServiceCaseCommunicationThreadTabProps) {
  const {
    data: messages,
    isLoading,
    isError,
  } = useGetServiceCaseCustomerMessagesQuery({
    serviceCaseId: props.serviceCaseId,
  });
  const {activeFilter, setActiveFilter, filterCounts, groups} = useCommunicationThreadMessages(
    messages?.messages ?? []
  );
  const {callPairing, canEditCallNotes, canOpenCall} = useThreadCallActions({
    resourceId: EntityResourceIds.serviceCase,
    recordId: props.serviceCaseId,
  });

  const customerId =
    props.serviceCase?.contactInformation?.customerId ?? props.serviceCase?.customerId;
  const contextLinkLabels = Object.fromEntries([
    [props.serviceCaseId, props.serviceCase?.number ?? undefined],
    ...(props.serviceOrders ?? []).map((order) => [order.serviceOrderId, order.number]),
  ]);
  const defaultPhoneNumber = getServiceCaseContactPhoneNumber(
    props.serviceCase?.contactInformation?.phoneNumber?.prefix,
    props.serviceCase?.contactInformation?.phoneNumber?.number
  );

  return (
    <VStack height="100%">
      <Box
        paddingHorizontal={4}
        paddingVertical={3}
        backgroundColor="general.white"
        borderBottom="1px solid"
        borderColor="general.separator"
      >
        <CommunicationChannelChips
          filterCounts={filterCounts}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />
      </Box>

      <Box flex={1} overflow="auto" minHeight={0}>
        <Box paddingHorizontal={4} paddingVertical={4}>
          <CommunicationThreadFeed
            groups={groups}
            isLoading={isLoading}
            isError={isError}
            contextLinkHostRecord={{
              recordId: props.serviceCaseId,
              resourceId: EntityResourceIds.serviceCase,
            }}
            contextLinkLabels={contextLinkLabels}
            includeHostContextLink
            preferPrimaryContextLink
            showOutgoingRecipient
            callPairing={callPairing}
            canEditCallNotes={canEditCallNotes}
            canOpenCall={canOpenCall}
          />
        </Box>
      </Box>

      <Show when={isNotNilOrEmpty(customerId)}>
        <Box
          paddingHorizontal={4}
          paddingVertical={3}
          backgroundColor="general.white"
          borderTop="1px solid"
          borderColor="general.separator"
        >
          <CommunicationThreadComposer
            customerId={customerId ?? ''}
            recordId={props.serviceCaseId}
            resourceId={EntityResourceIds.serviceCase}
            secondaryRecordIds={[]}
            defaultEmailAddress={props.serviceCase?.contactInformation?.email}
            defaultPhoneNumber={defaultPhoneNumber}
          />
        </Box>
      </Show>
    </VStack>
  );
}
